import { widgetSpan } from './widgets/registry'

// Deterministic layout for a wallboard screen on a fine 12 × 6 cell grid.
// Widgets carry free spans (cols/rows, resized by dragging on the editor
// canvas) and optional pinned positions (col/row). A pinned position is kept
// when it still fits; everything else — and any pin that now collides, e.g.
// after a resize — packs first-fit. The display and the editor canvas share
// this, so what you arrange is exactly what the TV shows, and overlap is
// impossible by construction.
export const GRID_COLS = 12
export const GRID_ROWS = 6
export const MIN_COLS = 2
export const MIN_ROWS = 2

const clamp = (value, min, max) => Math.min(Math.max(value, min), max)

// Legacy size presets ('2x1' etc.) were spans on the old 4 × 2 grid — scale
// by 3 so boards saved before free resizing keep their proportions
const LEGACY_SCALE = 3
export const presetToSpan = (size) => {
  const { cols, rows } = widgetSpan(size)
  return {
    cols: clamp(cols * LEGACY_SCALE, MIN_COLS, GRID_COLS),
    rows: clamp(rows * LEGACY_SCALE, MIN_ROWS, GRID_ROWS),
  }
}

export const spanOf = (widget = {}) => {
  if (Number.isInteger(widget.cols) && Number.isInteger(widget.rows)) {
    return {
      cols: clamp(widget.cols, MIN_COLS, GRID_COLS),
      rows: clamp(widget.rows, MIN_ROWS, GRID_ROWS),
    }
  }
  return presetToSpan(widget.size)
}

export const rectsOverlap = (a, b) =>
  a.col < b.col + b.cols &&
  b.col < a.col + a.cols &&
  a.row < b.row + b.rows &&
  b.row < a.row + a.rows

// Returns [{ widget, index, col, row, cols, rows }] in original widget order.
// Widgets that don't fit anywhere come back with col/row === null — the
// display skips them, the editor warns about them.
export const layoutScreen = (widgets = []) => {
  const occupied = Array.from({ length: GRID_ROWS }, () =>
    Array(GRID_COLS).fill(false),
  )

  const fits = (col, row, cols, rows) => {
    if (col < 0 || row < 0 || col + cols > GRID_COLS || row + rows > GRID_ROWS)
      return false
    for (let r = row; r < row + rows; r++) {
      for (let c = col; c < col + cols; c++) {
        if (occupied[r][c]) return false
      }
    }
    return true
  }

  const claim = (col, row, cols, rows) => {
    for (let r = row; r < row + rows; r++) {
      for (let c = col; c < col + cols; c++) {
        occupied[r][c] = true
      }
    }
  }

  const placed = []
  const auto = []

  widgets.forEach((widget, index) => {
    const { cols, rows } = spanOf(widget)
    if (
      Number.isInteger(widget.col) &&
      Number.isInteger(widget.row) &&
      fits(widget.col, widget.row, cols, rows)
    ) {
      claim(widget.col, widget.row, cols, rows)
      placed.push({ widget, index, col: widget.col, row: widget.row, cols, rows })
    } else {
      auto.push({ widget, index, cols, rows })
    }
  })

  for (const item of auto) {
    let done = false
    for (let row = 0; row < GRID_ROWS && !done; row++) {
      for (let col = 0; col <= GRID_COLS - item.cols && !done; col++) {
        if (fits(col, row, item.cols, item.rows)) {
          claim(col, row, item.cols, item.rows)
          placed.push({ ...item, col, row })
          done = true
        }
      }
    }
    if (!done) placed.push({ ...item, col: null, row: null })
  }

  return placed.sort((a, b) => a.index - b.index)
}
